import React, { useCallback, useEffect, useState } from 'react';
import { req } from '../services/api';
import Modal from '../components/Modal';

const PAGE_SIZE = 25;

const PAYOUT_LABELS = {
  bank: 'Bankovní převod',
  paypal: 'PayPal',
  revolut: 'Revolut'
};

const money = (value, currency = 'EUR') =>
  Number(value || 0).toFixed(2) + ' ' + (currency === 'EUR' ? '€' : currency);

const formatDateTime = value => {
  if (!value) return '—';
  return new Date(value).toLocaleString('cs-CZ');
};

export default function Payouts() {
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);

  const [loading, setLoading] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPages, setHistoryPages] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyType, setHistoryType] = useState('');

  const [payoutModal, setPayoutModal] = useState(false);
  const [currentRecipient, setCurrentRecipient] = useState(null);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const payoutDetail = recipient => {
    if (!recipient) return null;

    if (recipient.payoutMethod === 'bank') {
      return recipient.iban || null;
    }

    if (recipient.payoutMethod === 'paypal') {
      return recipient.paypalEmail || null;
    }

    if (recipient.payoutMethod === 'revolut') {
      return recipient.iban || null;
    }

    return null;
  };

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const historyParams = new URLSearchParams({
        page: String(historyPage),
        pageSize: String(PAGE_SIZE)
      });
  
      if (historyType) {
        historyParams.set('recipientType', historyType);
      }
  
      const [pendingPayouts, payoutHistory] = await Promise.all([
        req('GET', '/payouts/pending'),
        req('GET', '/payouts?' + historyParams.toString())
      ]);

      const driverPending = Array.isArray(pendingPayouts?.drivers)
        ? pendingPayouts.drivers.map(driver => ({
            ...driver,
            recipientType: 'DRIVER',
            recipientName:
              driver.user?.name ||
              driver.companyName ||
              '\u0158idi\u010d'
          }))
        : [];

      const fleetPending = Array.isArray(pendingPayouts?.fleets)
        ? pendingPayouts.fleets.map(fleet => ({
            ...fleet,
            recipientType: 'FLEET',
            recipientName:
              fleet.companyName ||
              fleet.user?.name ||
              'Flotila'
          }))
        : [];

      setPending([...driverPending, ...fleetPending]);
  
      if (
        payoutHistory &&
        Array.isArray(payoutHistory.payouts)
      ) {
        setHistory(payoutHistory.payouts);
        setHistoryTotal(payoutHistory.total || 0);
        setHistoryPages(payoutHistory.totalPages || 1);
      } else {
        setHistory([]);
        setHistoryTotal(0);
        setHistoryPages(1);
      }
    } catch (error) {
      console.error('Payouts load failed:', error);

      setPending([]);
      setHistory([]);
      setHistoryTotal(0);
      setHistoryPages(1);

      alert(
        'Chyba p\u0159i na\u010d\u00edt\u00e1n\u00ed v\u00fdplat: ' +
          (error?.message || 'nezn\u00e1m\u00e1 chyba')
      );
    } finally {
      setLoading(false);
    }
  }, [historyPage, historyType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openPayout = recipient => {
    setCurrentRecipient(recipient);
    setPaymentConfirmed(false);
    setSubmitting(false);
    setPayoutModal(true);
  };

  const confirmPayout = async () => {
    if (!currentRecipient || !paymentConfirmed) {
      return;
    }

    const detail = payoutDetail(currentRecipient);

    if (!detail) {
      alert(
        'Příjemce nemá vyplněné údaje pro zvolenou výplatní metodu.'
      );
      return;
    }

    if (
      !window.confirm(
        'Potvrzujete, že manuální platba ve výši ' +
          money(currentRecipient.walletBalance) +
          ' již byla skutečně provedena? Potvrzením se zůstatek v EURO.TAXI vynuluje a vytvoří se záznam o výplatě.'
      )
    ) {
      return;
    }

    setSubmitting(true);

    try {
      const endpoint =
        currentRecipient.recipientType === 'FLEET'
          ? '/fleets/' + currentRecipient.id + '/payout'
          : '/drivers/' + currentRecipient.id + '/payout';

      const result = await req('POST', endpoint, {});

      if (!result?.success) {
        alert(
          'Výplatu se nepodařilo potvrdit: ' +
            (result?.error || 'neznámá chyba')
        );
        setSubmitting(false);
        return;
      }

      setPayoutModal(false);
      setCurrentRecipient(null);
      setPaymentConfirmed(false);

      await loadData();

      alert(
        '✓ Výplata byla zaevidována a zůstatek příjemce byl vynulován.'
      );
    } catch (error) {
      alert(
        'Výplatu se nepodařilo potvrdit: ' +
          (error?.message || 'neznámá chyba')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const pendingDrivers = pending.filter(
    item => item.recipientType === 'DRIVER'
  ).length;

  const pendingFleets = pending.filter(
    item => item.recipientType === 'FLEET'
  ).length;

  const pendingAmount = pending.reduce(
    (sum, item) =>
      sum + Number(item.walletBalance || 0),
    0
  );

  return (
    <div>
      <div className="page-title">Výplaty</div>

      <div className="page-sub">
        Výplaty samostatným řidičům a flotilám, platební údaje a historie
      </div>

      <div className="stats-grid">
        <div
          className={
            pending.length > 0
              ? 'stat-card alert'
              : 'stat-card'
          }
        >
          <div className="stat-value">
            {pending.length}
          </div>
          <div className="stat-label">
            Čekající výplaty
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-value">
            {pendingDrivers}
          </div>
          <div className="stat-label">
            Samostatní řidiči
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-value">
            {pendingFleets}
          </div>
          <div className="stat-label">
            Flotily
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-value">
            {money(pendingAmount)}
          </div>
          <div className="stat-label">
            Celkem k výplatě
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-value">
            {historyTotal}
          </div>
          <div className="stat-label">
            Provedené výplaty
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">
            Čekající výplaty
          </span>

          <span className="dashboard-muted">
            {pending.length} příjemců · {money(pendingAmount)}
          </span>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Příjemce</th>
                <th>Typ</th>
                <th>E-mail</th>
                <th>Výplatní metoda</th>
                <th>Platební údaj</th>
                <th>Zůstatek</th>
                <th>Akce</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="7"
                    className="loading-cell"
                  >
                    Načítám...
                  </td>
                </tr>
              ) : pending.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="empty-cell"
                  >
                    Žádné čekající výplaty
                  </td>
                </tr>
              ) : (
                pending.map(recipient => {
                  const detail =
                    payoutDetail(recipient);

                  return (
                    <tr
                      key={
                        recipient.recipientType +
                        '-' +
                        recipient.id
                      }
                    >
                      <td>
                        <strong>
                          {recipient.recipientName}
                        </strong>

                        {recipient.recipientType ===
                          'FLEET' &&
                          recipient.ico && (
                            <>
                              <br />
                              <span className="table-secondary">
                                IČO {recipient.ico}
                              </span>
                            </>
                          )}
                      </td>

                      <td>
                        <span
                          className={
                            'badge ' +
                            (recipient.recipientType ===
                            'FLEET'
                              ? 'badge-blue'
                              : 'badge-gold')
                          }
                        >
                          {recipient.recipientType ===
                          'FLEET'
                            ? 'Flotila'
                            : 'Řidič'}
                        </span>
                      </td>

                      <td className="table-secondary">
                        {recipient.user?.email || '—'}
                      </td>

                      <td>
                        {PAYOUT_LABELS[
                          recipient.payoutMethod
                        ] ||
                          recipient.payoutMethod ||
                          '—'}
                      </td>

                      <td>
                        {detail ? (
                          <span
                            style={{
                              wordBreak: 'break-all'
                            }}
                          >
                            {detail}
                          </span>
                        ) : (
                          <span className="badge badge-red">
                            Chybí údaje
                          </span>
                        )}
                      </td>

                      <td>
                        <strong className="gold-text">
                          {money(
                            recipient.walletBalance
                          )}
                        </strong>
                      </td>

                      <td>
                        <button
                          className={
                            'btn-sm ' +
                            (detail
                              ? 'btn-green'
                              : 'btn-gray')
                          }
                          disabled={!detail}
                          style={{
                            opacity: detail ? 1 : 0.5
                          }}
                          onClick={() =>
                            openPayout(recipient)
                          }
                        >
                          Vyplatit
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">
            Historie výplat
          </span>

          <span className="dashboard-muted">
            {historyTotal} záznamů
          </span>
        </div>

        <div className="admin-toolbar">
          <select
            value={historyType}
            onChange={event => {
              setHistoryType(
                event.target.value
              );
              setHistoryPage(1);
            }}
          >
            <option value="">
              Všichni příjemci
            </option>

            <option value="DRIVER">
              Samostatní řidiči
            </option>

            <option value="FLEET">
              Flotily
            </option>
          </select>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Datum</th>
                <th>Příjemce</th>
                <th>Typ</th>
                <th>Metoda</th>
                <th>Platební údaj</th>
                <th>Částka</th>
                <th>ID výplaty</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="7"
                    className="loading-cell"
                  >
                    Načítám...
                  </td>
                </tr>
              ) : history.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="empty-cell"
                  >
                    Historie výplat je prázdná
                  </td>
                </tr>
              ) : (
                history.map(payout => (
                  <tr key={payout.id}>
                    <td>
                      {formatDateTime(
                        payout.createdAt
                      )}
                    </td>

                    <td>
                      <strong>
                        {payout.recipientName ||
                          '—'}
                      </strong>
                    </td>

                    <td>
                      <span
                        className={
                          'badge ' +
                          (payout.recipientType ===
                          'FLEET'
                            ? 'badge-blue'
                            : 'badge-gold')
                        }
                      >
                        {payout.recipientType ===
                        'FLEET'
                          ? 'Flotila'
                          : 'Řidič'}
                      </span>
                    </td>

                    <td>
                      {PAYOUT_LABELS[
                        payout.payoutMethod
                      ] ||
                        payout.payoutMethod ||
                        '—'}
                    </td>

                    <td
                      style={{
                        wordBreak: 'break-all'
                      }}
                    >
                      {payout.payoutDetail || '—'}
                    </td>

                    <td>
                      <strong className="gold-text">
                        {money(
                          payout.amount,
                          payout.currency
                        )}
                      </strong>
                    </td>

                    <td className="table-secondary">
                      {payout.id}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && historyPages > 1 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 12,
              padding: '16px 0'
            }}
          >
            <button
              className="btn-sm btn-gray"
              disabled={historyPage === 1}
              style={{
                opacity:
                  historyPage === 1 ? 0.4 : 1
              }}
              onClick={() =>
                setHistoryPage(page =>
                  Math.max(1, page - 1)
                )
              }
            >
              ← Předchozí
            </button>

            <span className="table-secondary">
              Stránka {historyPage} z{' '}
              {historyPages}
            </span>

            <button
              className="btn-sm btn-gray"
              disabled={
                historyPage === historyPages
              }
              style={{
                opacity:
                  historyPage === historyPages
                    ? 0.4
                    : 1
              }}
              onClick={() =>
                setHistoryPage(page =>
                  Math.min(
                    historyPages,
                    page + 1
                  )
                )
              }
            >
              Další →
            </button>
          </div>
        )}
      </div>

      {payoutModal && currentRecipient && (
        <Modal
          title={
            'Výplata – ' +
            currentRecipient.recipientName
          }
          onClose={() =>
            !submitting &&
            setPayoutModal(false)
          }
          actions={
            <>
              <button
                className="btn-gray"
                disabled={submitting}
                onClick={() =>
                  setPayoutModal(false)
                }
              >
                Zrušit
              </button>

              <button
                className="btn"
                disabled={
                  !paymentConfirmed ||
                  submitting
                }
                style={{
                  opacity:
                    paymentConfirmed &&
                    !submitting
                      ? 1
                      : 0.45
                }}
                onClick={confirmPayout}
              >
                {submitting
                  ? 'Zpracovávám...'
                  : 'Potvrdit jako vyplacené'}
              </button>
            </>
          }
        >
          <div className="payout-amount-box">
            <div
              style={{
                color: 'var(--gray)',
                fontSize: 11,
                textTransform: 'uppercase',
                letterSpacing: 1,
                marginBottom: 6
              }}
            >
              Částka k vyplacení
            </div>

            <div
              style={{
                color: 'var(--gold)',
                fontSize: 36,
                fontWeight: 900
              }}
            >
              {money(
                currentRecipient.walletBalance
              )}
            </div>
          </div>

          <div className="payout-detail-box">
            <div className="payout-row">
              <span className="payout-row-label">
                Příjemce
              </span>

              <span className="payout-row-value">
                {currentRecipient.recipientName}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Typ
              </span>

              <span className="payout-row-value">
                {currentRecipient.recipientType ===
                'FLEET'
                  ? 'Flotila'
                  : 'Samostatný řidič'}
              </span>
            </div>

            <div className="payout-row">
              <span className="payout-row-label">
                Metoda
              </span>

              <span className="payout-row-value">
                {PAYOUT_LABELS[
                  currentRecipient.payoutMethod
                ] ||
                  currentRecipient.payoutMethod ||
                  '—'}
              </span>
            </div>

            {currentRecipient.payoutMethod ===
              'bank' && (
              <>
                <div className="payout-row">
                  <span className="payout-row-label">
                    IBAN
                  </span>

                  <span
                    className="payout-row-value"
                    style={{
                      wordBreak: 'break-all',
                      textAlign: 'right',
                      maxWidth: '65%'
                    }}
                  >
                    {currentRecipient.iban || '—'}
                  </span>
                </div>

                <div className="payout-row">
                  <span className="payout-row-label">
                    Majitel účtu
                  </span>

                  <span className="payout-row-value">
                    {currentRecipient.accountOwner ||
                      '—'}
                  </span>
                </div>
              </>
            )}

            {currentRecipient.payoutMethod ===
              'paypal' && (
              <div className="payout-row">
                <span className="payout-row-label">
                  PayPal
                </span>

                <span className="payout-row-value">
                  {currentRecipient.paypalEmail ||
                    '—'}
                </span>
              </div>
            )}

            {currentRecipient.payoutMethod ===
              'revolut' && (
              <>
                <div className="payout-row">
                  <span className="payout-row-label">
                    IBAN
                  </span>

                  <span
                    className="payout-row-value"
                    style={{
                      wordBreak: 'break-all',
                      textAlign: 'right',
                      maxWidth: '65%'
                    }}
                  >
                    {currentRecipient.iban || '\u2014'}
                  </span>
                </div>

                <div className="payout-row">
                  <span className="payout-row-label">
                    {'Majitel \u00fa\u010dtu'}
                  </span>

                  <span className="payout-row-value">
                    {currentRecipient.accountOwner || '\u2014'}
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="warning-box">
            Tato administrace peníze sama neodesílá. Nejdříve proveďte platbu ručně podle údajů výše. Teprve potom ji zde potvrďte. Potvrzením se zůstatek příjemce v systému vynuluje a vytvoří se záznam v historii.
          </div>

          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              marginTop: 16,
              cursor: 'pointer'
            }}
          >
            <input
              type="checkbox"
              checked={paymentConfirmed}
              onChange={event =>
                setPaymentConfirmed(
                  event.target.checked
                )
              }
              style={{
                width: 18,
                height: 18,
                marginTop: 2
              }}
            />

            <span>
              Potvrzuji, že manuální platba ve výši{' '}
              <strong className="gold-text">
                {money(
                  currentRecipient.walletBalance
                )}
              </strong>{' '}
              již byla skutečně provedena.
            </span>
          </label>
        </Modal>
      )}
    </div>
  );
}
